// frontend/modules/service-metrics/components/RecentErrorsCard.tsx
import React from 'react';
import { Card, List, Tag, Typography, Empty, Badge } from 'antd';
import { ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import type { ErrorSummary } from '../types/service-metrics.types';

const { Text, Paragraph } = Typography;

interface RecentErrorsCardProps {
    errors: ErrorSummary[];
    loading?: boolean;
    title?: string;
}

const RecentErrorsCard: React.FC<RecentErrorsCardProps> = ({ 
    errors, 
    loading,
    title = 'Recent Errors'
}) => {
    const { dark } = useTheme();

    if (!errors || errors.length === 0) {
        return (
            <Card 
                title={title} 
                loading={loading}
                className={dark ? 'bg-gray-800' : ''}
            >
                <Empty 
                    description="No errors in the selected period" 
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            </Card>
        );
    }

    return (
        <Card 
            title={
                <span>
                    {title}
                    <Badge count={errors.length} style={{ marginLeft: 8 }} />
                </span>
            }
            loading={loading}
            className={dark ? 'bg-gray-800' : ''}
        >
            <List
                dataSource={errors}
                renderItem={(error) => (
                    <List.Item>
                        <List.Item.Meta
                            avatar={
                                <ExclamationCircleOutlined 
                                    style={{ fontSize: 20, color: '#ff4d4f' }} 
                                />
                            }
                            title={
                                <span>
                                    <Tag color="error">{error.errorType}</Tag>
                                    <Tag color="default">{error.count} occurrences</Tag>
                                </span>
                            }
                            description={
                                <>
                                    <Paragraph 
                                        ellipsis={{ rows: 2 }} 
                                        style={{ marginBottom: 4 }}
                                    >
                                        {error.errorMessage || 'No error message'}
                                    </Paragraph>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        <ClockCircleOutlined style={{ marginRight: 4 }} />
                                        Last: {error.lastOccurrence ? new Date(error.lastOccurrence).toLocaleString() : 'Unknown'}
                                    </Text>
                                </>
                            }
                        />
                    </List.Item>
                )}
            />
        </Card>
    );
};

export default RecentErrorsCard;
